/**
 * /financeiro/fluxo-caixa — Fluxo de Caixa v2 (mockup Fase 5 V2.2).
 *
 * Visão calendário: cada dia mostra os eventos (entrada/saída realizada,
 * programado) + total do dia. KPI strip (Saldo hoje / A receber / A pagar /
 * Saldo projetado fim do mês / Dia crítico) + sugestão heurística honesta.
 * Abas Mês / Semana / Lista.
 *
 * 100% construído no frontend a partir de /api/lancamentos do período +
 * /api/financeiro/resumo (saldo). NENHUMA mudança de lógica/backend — o evento
 * cai no dia da data_caixa (realizado) ou data_competencia (programado).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FiChevronLeft, FiChevronRight, FiZap } from "react-icons/fi";

import { useAuth } from "../../../context/AuthContext";
import {
  formatarReais, listarLancamentos, obterResumoFinanceiro,
} from "../../../services/financeiro";
import { PageContainer } from "../../../styles/shared";

const MESES = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];
const DOW = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

function isoLocal(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
// "R$ 1.234" compacto p/ badges do dia
function fmtK(v) {
  const n = Number(v || 0);
  const a = Math.abs(n);
  const s = n < 0 ? "−" : "+";
  if (a >= 1000) return `${s}${(a / 1000).toLocaleString("pt-BR", { maximumFractionDigits: 1 })}k`;
  return `${s}${a.toLocaleString("pt-BR", { maximumFractionDigits: 0 })}`;
}


export default function FluxoCaixa() {
  const { request } = useAuth();
  const hoje = useMemo(() => new Date(), []);
  const hojeISO = useMemo(() => isoLocal(hoje), [hoje]);

  const [ref, setRef] = useState(() => ({ ano: hoje.getFullYear(), mes: hoje.getMonth() }));
  const [aba, setAba] = useState("mes");
  const [lancs, setLancs] = useState([]);
  const [saldoHoje, setSaldoHoje] = useState(0);
  const [error, setError] = useState(null);

  const carregar = useCallback(async () => {
    setError(null);
    try {
      const [payload, resumo] = await Promise.all([
        listarLancamentos(request, {}),
        obterResumoFinanceiro(request).catch(() => null),
      ]);
      setLancs(Array.isArray(payload) ? payload : payload?.results || []);
      if (resumo) setSaldoHoje(Number(resumo.saldo_total_atual || 0));
    } catch (err) {
      setError(err?.message || "Erro ao carregar fluxo.");
    }
  }, [request]);

  useEffect(() => { carregar(); }, [carregar]);

  // Mapa dia -> eventos (data efetiva = caixa se realizado, senão competência)
  const eventosPorDia = useMemo(() => {
    const map = new Map();
    for (const l of lancs) {
      const realizado = !!l.data_caixa;
      const dia = realizado ? l.data_caixa : l.data_competencia;
      if (!dia) continue;
      const entrada = l.tipo === "ENTRADA";
      const signed = entrada ? Number(l.valor || 0) : -Number(l.valor || 0);
      const ev = {
        label: l.descricao,
        valor: Number(l.valor || 0),
        signed,
        kind: realizado ? (entrada ? "income" : "expense") : "scheduled",
      };
      if (!map.has(dia)) map.set(dia, []);
      map.get(dia).push(ev);
    }
    return map;
  }, [lancs]);

  // KPIs
  const kpis = useMemo(() => {
    const fimMes = isoLocal(new Date(ref.ano, ref.mes + 1, 0));
    let aReceber = 0, aPagar = 0, projetadoDelta = 0;
    const netFuturo = new Map();
    for (const l of lancs) {
      if (l.data_caixa) continue; // só em aberto
      const comp = l.data_competencia;
      if (!comp || comp < hojeISO) continue;
      const entrada = l.tipo === "ENTRADA";
      const v = Number(l.valor || 0);
      if (entrada) aReceber += v; else aPagar += v;
      if (comp <= fimMes) projetadoDelta += entrada ? v : -v;
      const signed = entrada ? v : -v;
      netFuturo.set(comp, (netFuturo.get(comp) || 0) + signed);
    }
    // dia mais crítico = dia futuro com net mais negativo
    let critDia = null, critVal = 0;
    for (const [dia, net] of netFuturo) {
      if (net < critVal) { critVal = net; critDia = dia; }
    }
    return {
      saldoHoje, aReceber, aPagar,
      projetado: saldoHoje + projetadoDelta,
      critDia, critVal,
    };
  }, [lancs, ref, saldoHoje, hojeISO]);

  // Sugestão heurística honesta: se há um dia crítico, procura uma entrada
  // programada posterior cujo valor cubra >=80% do rombo — sugere antecipar.
  const sugestao = useMemo(() => {
    if (!kpis.critDia || kpis.critVal >= 0) return null;
    const rombo = Math.abs(kpis.critVal);
    const candidatos = lancs
      .filter((l) => !l.data_caixa && l.tipo === "ENTRADA"
        && l.data_competencia > kpis.critDia)
      .sort((a, b) => Number(b.valor) - Number(a.valor));
    const c = candidatos.find((l) => Number(l.valor) >= rombo * 0.8);
    if (!c) return null;
    const [, m, d] = kpis.critDia.split("-");
    return `O dia ${d}/${m} fica negativo em ${formatarReais(rombo)}. `
      + `Antecipar “${c.descricao}” (${formatarReais(c.valor)}) cobre `
      + `${Math.round(Number(c.valor) / rombo * 100)}% do vale sem usar conta garantida.`;
  }, [kpis, lancs]);

  // Construção do grid
  const dias = useMemo(() => {
    if (aba === "lista") return null;
    if (aba === "semana") {
      const base = new Date(hoje); base.setDate(base.getDate() - base.getDay());
      return Array.from({ length: 7 }, (_, i) => {
        const dt = new Date(base); dt.setDate(base.getDate() + i); return dt;
      });
    }
    const primeiro = new Date(ref.ano, ref.mes, 1);
    const start = new Date(primeiro); start.setDate(1 - primeiro.getDay());
    return Array.from({ length: 42 }, (_, i) => {
      const dt = new Date(start); dt.setDate(start.getDate() + i); return dt;
    });
  }, [aba, ref, hoje]);

  // Lista plana (aba Lista): eventos do mês ordenados por dia
  const listaEventos = useMemo(() => {
    if (aba !== "lista") return [];
    const out = [];
    for (const [dia, evs] of eventosPorDia) {
      if (dia.slice(0, 7) !== `${ref.ano}-${String(ref.mes + 1).padStart(2, "0")}`) continue;
      evs.forEach((e) => out.push({ ...e, dia }));
    }
    return out.sort((a, b) => a.dia.localeCompare(b.dia));
  }, [aba, eventosPorDia, ref]);

  const navMes = (delta) => setRef((r) => {
    const m = r.mes + delta;
    if (m < 0) return { ano: r.ano - 1, mes: 11 };
    if (m > 11) return { ano: r.ano + 1, mes: 0 };
    return { ano: r.ano, mes: m };
  });

  return (
    <PageContainer style={{ maxWidth: "none" }} className="fin2">
      <div className="flex between" style={{ alignItems: "flex-end", marginBottom: 18, flexWrap: "wrap", gap: 12 }}>
        <div>
          <div className="breadcrumb">
            <span>Início</span><span className="crumb-sep">/</span>
            <span>Financeiro</span><span className="crumb-sep">/</span>
            <span className="active">Fluxo de Caixa</span>
          </div>
          <h1 className="page-title">Fluxo de Caixa</h1>
          <div className="page-sub">Visão calendário · entradas, saídas e projeções</div>
        </div>
        <div className="tabchip-group">
          {[{ id: "mes", l: "Mês" }, { id: "semana", l: "Semana" }, { id: "lista", l: "Lista" }].map((t) => (
            <div key={t.id} className={"tabchip" + (aba === t.id ? " active" : "")}
                 role="button" tabIndex={0} onClick={() => setAba(t.id)}
                 onKeyDown={(e) => e.key === "Enter" && setAba(t.id)}>{t.l}</div>
          ))}
        </div>
      </div>

      <div className="kpi-strip-v2 standalone" style={{ marginBottom: 14 }}>
        <div className="kpi-v2"><div className="lbl">Saldo hoje</div><div className="val">{formatarReais(kpis.saldoHoje)}</div><div className="meta">{hoje.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" })}</div></div>
        <div className="kpi-v2"><div className="lbl">A receber · em aberto</div><div className="val" style={{ color: "var(--success-text)" }}>{formatarReais(kpis.aReceber)}</div><div className="meta">previsto</div></div>
        <div className="kpi-v2"><div className="lbl">A pagar · em aberto</div><div className="val" style={{ color: "var(--danger-text)" }}>{formatarReais(kpis.aPagar)}</div><div className="meta">previsto</div></div>
        <div className="kpi-v2"><div className="lbl">Saldo projetado fim do mês</div><div className="val">{formatarReais(kpis.projetado)}</div><div className="meta">com programados</div></div>
        <div className="kpi-v2"><div className="lbl">Dia mais crítico</div>
          <div className="val" style={{ fontSize: 15 }}>{kpis.critDia ? kpis.critDia.split("-").reverse().slice(0, 2).join("/") : "—"}</div>
          <div className="meta">{kpis.critVal < 0 ? formatarReais(kpis.critVal) : "sem vale"}</div>
        </div>
        {sugestao && (
          <div className="next-action" style={{ background: "var(--info-bg)", borderLeftColor: "var(--info-border)" }}>
            <div className="lbl" style={{ color: "var(--info-text)" }}><FiZap size={13} aria-hidden="true" /> SUGESTÃO</div>
            <div className="title" style={{ color: "var(--info-text)" }}>{sugestao}</div>
          </div>
        )}
      </div>

      {error && <div className="empty"><div className="msg">{error}</div></div>}

      {/* ===== LISTA ===== */}
      {aba === "lista" ? (
        <div className="table-wrap">
          <table className="dtable">
            <colgroup><col style={{ width: 90 }} /><col /><col style={{ width: 140 }} /><col style={{ width: 120 }} /></colgroup>
            <thead><tr><th>Data</th><th>Evento</th><th>Situação</th><th className="num" style={{ textAlign: "right" }}>Valor</th></tr></thead>
            <tbody>
              {listaEventos.length === 0 && (
                <tr><td colSpan={4} style={{ padding: 0 }}><div className="empty"><div className="msg">Nenhum evento no mês.</div></div></td></tr>
              )}
              {listaEventos.map((e, i) => (
                <tr key={i}>
                  <td className="mono">{e.dia.split("-").reverse().slice(0, 2).join("/")}</td>
                  <td className="ell" title={e.label}>{e.label}</td>
                  <td>{e.kind === "scheduled" ? <span className="badge info no-dot">Programado</span> : <span className="badge success no-dot">Realizado</span>}</td>
                  <td className="num" style={{ textAlign: "right", fontWeight: 600, color: e.signed >= 0 ? "var(--success-text)" : "var(--danger-text)" }}>{fmtK(e.signed)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        /* ===== CALENDÁRIO (Mês / Semana) ===== */
        <div className="calendar">
          <div className="calendar__head">
            <div className="calendar__nav">
              {aba === "mes" && (
                <button type="button" className="icon-btn" style={{ width: 32, height: 32 }} onClick={() => navMes(-1)}>
                  <FiChevronLeft size={16} aria-hidden="true" />
                </button>
              )}
              <div className="calendar__title">
                {aba === "semana" ? "Semana atual" : `${MESES[ref.mes]} ${ref.ano}`}
              </div>
              {aba === "mes" && (
                <button type="button" className="icon-btn" style={{ width: 32, height: 32 }} onClick={() => navMes(1)}>
                  <FiChevronRight size={16} aria-hidden="true" />
                </button>
              )}
            </div>
            <div className="legend-row">
              <span className="legend-item"><span className="dot" style={{ background: "var(--success-solid)" }} /> Entrada</span>
              <span className="legend-item"><span className="dot" style={{ background: "var(--danger-solid)" }} /> Saída</span>
              <span className="legend-item"><span className="dot" style={{ background: "var(--info-solid)" }} /> Programado</span>
            </div>
          </div>
          <div className="calendar__grid">
            {DOW.map((d) => <div key={d} className="dow">{d}</div>)}
            {dias.map((dt, i) => {
              const di = isoLocal(dt);
              const evs = eventosPorDia.get(di) || [];
              const net = evs.reduce((a, e) => a + e.signed, 0);
              const cls = ["calendar__cell"];
              if (aba === "mes" && dt.getMonth() !== ref.mes) cls.push("other-month");
              if (dt.getDay() === 0 || dt.getDay() === 6) cls.push("weekend");
              if (di === hojeISO) cls.push("today");
              return (
                <div key={i} className={cls.join(" ")}>
                  <div className="calendar__day">
                    {dt.getDate()}
                    {evs.length > 0 && (
                      <span className={"total " + (net > 0 ? "up" : net < 0 ? "down" : "flat")}>{fmtK(net)}</span>
                    )}
                  </div>
                  {evs.slice(0, 3).map((e, j) => (
                    <div key={j} className={"cal-event " + e.kind} title={e.label}>
                      {e.label} <span className="v">{fmtK(e.signed)}</span>
                    </div>
                  ))}
                  {evs.length > 3 && <div className="tiny muted" style={{ padding: "0 2px" }}>+{evs.length - 3}</div>}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </PageContainer>
  );
}
